import { SCREEN_PRESENTER } from '@shoflo/polymer';
import { withCurrentParams } from 'actions/utils';
import { BROADCAST_UPDATE_REQUEST } from 'actions/Types';
import { DIRECTOR_REQUEST_ANSWER } from 'store/eventConstants';
import { increaseScreenShare } from '@shoflo/studio-redux/actions';
import {
  updateSource,
  setUnassignedAttendeePresence,
  setIsInitializing,
  createSource
} from 'actions/Broadcast';
import { selectLastPresenterSource } from 'selectors/broadcastSourcesSelector';
import { ACCEPTED, SCREENSHARE } from 'constants/KnockTypes';
import { VENDOR_AGORA } from '@studio/constants';
import { shouldHaveFeature } from '@studio/feature.flags';

const DEFAULT_ORDER_NUMBER = 0;

/**
 * Action to update a director request (approve or deny)
 * @param request - Director request
 * @param changes - Object that contains the status update
 */
export default (request, changes) => {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState, socket) => {
      const { status } = changes;

      const state = getState();

      const {
        broadcastSources: { sources }
      } = state;

      const action = {
        type: BROADCAST_UPDATE_REQUEST,
        request: async apiClient => {
          // Get relevant information to update a director request
          const { name, type, _id: knockId, sourceId, attendeeId } = request;
          // Make a call to update the director request
          const { broadcast, ...restResponse } =
            await apiClient.broadcastKnock.updateKnockRequest(
              teamId,
              eventId,
              broadcastId,
              knockId,
              { name, type, status }
            );

          // With the Agora implementation, the source is created by the speaker
          // so we don't need to handle it here
          const useAgora =
            shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;
          if (useAgora) {
            const payload = {
              broadcastId: broadcast,
              attendeeId,
              sourceId,
              ...restResponse
            };
            socket.emit(DIRECTOR_REQUEST_ANSWER, payload);
            return payload;
          }

          if (type === SCREENSHARE && status === ACCEPTED) {
            dispatch(increaseScreenShare());
            // If assigned to a placeholder source, update source
            if (sourceId) {
              const value = `${attendeeId}#content`;
              dispatch(updateSource(sourceId, { value }));
              dispatch(setUnassignedAttendeePresence(value));
              dispatch(
                setIsInitializing(sourceId, {
                  isInitializing: true,
                  isAttendee: false
                })
              );
            } else {
              // Only create a new source if one does not already exist for this presenter's screenshare
              const screenShareValue = `${attendeeId}#content`;
              const existingScreenShareSource = sources.find(
                source => source.value === screenShareValue
              );
              if (!existingScreenShareSource) {
                let { order: lastPresenterOrder = DEFAULT_ORDER_NUMBER } =
                  selectLastPresenterSource(state);
                const order = lastPresenterOrder + 1;
                let title = `${attendeeId}`;
                const attendeeSource = sources.find(
                  source => source.value === attendeeId
                );
                if (attendeeSource && attendeeSource.title) {
                  title = attendeeSource.title;
                }
                dispatch(
                  createSource(
                    {
                      order,
                      title,
                      type: SCREEN_PRESENTER,
                      value: screenShareValue,
                      description: ''
                    },
                    true
                  )
                );
              }
            }
          }

          const payload = {
            broadcastId: broadcast,
            attendeeId,
            ...restResponse
          };
          // Sending approved or accepted request via socket to the presenter
          socket.emit(DIRECTOR_REQUEST_ANSWER, payload);
          // Return api response as payload to reducer
          return payload;
        }
      };

      dispatch(action);
    }
  );
};
